
import {
    PropType,
    computed,
    defineComponent,
    ref,
} from 'vue';
import { toWritableComputedProps } from 'src/util/composables/writable-computed-prop';
import { messages } from 'src/i18n/i18n';
import { RegExpInput } from 'src/components/RegExpInput';
import {
    SnfTokenInput,
} from '@redviking/argonaut-util/src/serial-numbers';
import { useEditing } from 'src/components/EntityDetail';

const alignItemMap: {
    [align in SnfTokenInput['align']]: {
        text: string,
        desc: string,
    }
} = {
    right: {
        text: messages.serialNum.serialNumber.alignments.right.text,
        desc: messages.serialNum.serialNumber.alignments.right.desc,
    },
    left: {
        text: messages.serialNum.serialNumber.alignments.left.text,
        desc: messages.serialNum.serialNumber.alignments.left.desc,
    },
};

const alignItemMapItems = Object.entries(alignItemMap).map(([ align, { text, desc } ]) => ({
    text,
    desc,
    value: align,
}));

const inputTokenConfigLengthErrorMessage = (len: number) => {
    if (len < 0 || len > 100) {
        return 'Length must be between 0 and 100';
    }
    return null;
};

const inputTokenConfigFillCharacterErrorMessage = (fill: string) => {
    if (!new RegExp(/^[0-9a-z]$/u, 'u').test(fill)) {
        return 'Character must match the following regex: /^[0-9a-z]$/ui';
    }
    return null;
};

export const inputTokenConfigErrorMessage = (config: SnfTokenInput) => {
    const lengthErrorMessage = inputTokenConfigLengthErrorMessage(config.len);
    if (lengthErrorMessage) {
        return lengthErrorMessage;
    }

    const fillErrorMessage = inputTokenConfigFillCharacterErrorMessage(config.fill);
    if (fillErrorMessage) {
        return fillErrorMessage;
    }

    return null;
};

export default defineComponent({
    name: 'InputToken',
    components: {
        RegExpInput,
    },
    props: {
        readonly: { type: Boolean, required: true },
        value: { type: Object as PropType<SnfTokenInput>, required: true },
    },
    setup (props, { emit }) {
        const editing = useEditing.useInject().ref;
        const token = computed({
            get: () => props.value,
            set: v => emit('input', v),
        });

        const {
            len,
            fill,
            align,
            validation,
        } = toWritableComputedProps(token);

        return {
            len,
            fill,
            align,
            token,
            editing,
            validation,
            alignItemMapItems,
            validationHintHover: ref(false),
            isValidLength: computed(() => inputTokenConfigLengthErrorMessage(len.value)),
            isValidFillCharacter: computed(() => inputTokenConfigFillCharacterErrorMessage(fill.value)),
        };
    },
});
